/**
 * Parties API slice — drives the deal-page "Parties" tab.
 *
 * One read endpoint (`getDealParties`) plus five mutations. The base URL
 * is rooted at `/api` because parties endpoints span two URL trees
 * (`/api/deals/:id/...` for deal-scoped surfaces and `/api/sides/:id/...`
 * for side-scoped mutations) — single-prefix base URLs (`/api/deals` or
 * `/api/sides`) wouldn't reach both. Tag invalidation is scoped by
 * `dealId` so a mutation only refetches the parties view for the deal
 * the user is looking at.
 *
 * UI-facing names use "Party" everywhere; backend route paths still use
 * `/sides/...` and the DB model is `Team` — those are server-internal
 * names. The mutation/hook names below were renamed Side→Party in the
 * Parties-tab iteration so the call sites read naturally
 * (`useCreatePartyMutation`, `useAddPartyMembersMutation`, etc.).
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface PartyMember {
  personId: string;
  personName: string | null;
  avatarRef: string | null;
  role: string | null;
  scope: string | null;
}

export interface Party {
  teamId: string;
  name: string;
  type: string | null;
  // REP-192 — party taxonomy + confidentiality side. `kind` drives the
  // human-facing label (see partyLabel); falls back to `name`.
  kind: string | null;
  sideKind: string | null;
  // REP-204 — a derived, read-only counterparty (the "mirror"): computed live
  // from the shared property, not stored. The UI shows it with no mutations.
  derived?: boolean;
  // REP-204 — for a derived counterparty: are we engaged? Drives the Engage CTA.
  engaged?: boolean;
  // Deal Room — a shared room exists (both sides mutually placed someone on a
  // doc). Reveal is FULL; the UI shows "In the room" instead of Engage.
  inRoom?: boolean;
  sourceProjectId: string | null;
  sourceProjectName: string | null;
  sourceProjectType: string | null;
  status: "ACTIVE" | "PASSED";
  members: PartyMember[];
  pursuitIds: string[];
}

export interface DealPartiesResult {
  dealId: string;
  scope: "PARENT_DEAL" | "PURSUIT_OR_STANDALONE";
  sides: Party[];
  pursuits: Array<{ id: string; name: string | null }>;
}

// REP-204 WS6 — effective-permissions ("who can see what, and why").
export interface EffectivePermissionDoc {
  documentId: string;
  documentName: string | null;
  accessLevels: string[];
  grantSource: string | null;
  sourceTeamId: string | null;
  sourceProjectId: string | null;
}
export interface EffectivePermissionPerson {
  personId: string;
  personName: string | null;
  sideKind: string | null;
  documents: EffectivePermissionDoc[];
}
export interface EffectivePermissionsResult {
  dealId: string;
  documentCount: number;
  people: EffectivePermissionPerson[];
}

export const partiesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "partiesApi",
    baseUrl: "/api",
  }),
  reducerPath: "partiesApi",
  tagTypes: ["DealParties"],
  endpoints: (builder) => ({
    getDealParties: builder.query<
      { data: DealPartiesResult },
      { dealId: string }
    >({
      query: ({ dealId }) => `/deals/${dealId}/parties`,
      providesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    getEffectivePermissions: builder.query<
      { data: EffectivePermissionsResult },
      { dealId: string }
    >({
      query: ({ dealId }) => `/deals/${dealId}/effective-permissions`,
      providesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    createParty: builder.mutation<any, { dealId: string; name: string }>({
      query: ({ dealId, name }) => ({
        url: `/deals/${dealId}/sides`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    renameParty: builder.mutation<
      any,
      { teamId: string; name: string; dealId: string }
    >({
      // Re-uses the existing PUT /api/team/:id endpoint exposed by the
      // singular `team` controller (not `teams`) — that one takes
      // `{ teamData: { name } }` and is the canonical rename surface.
      // The optional `dealId` query param lets the parties feature log a
      // `CONTACT_CREATED` activity row scoped to the deal (see
      // team.controller.ts).
      query: ({ teamId, name, dealId }) => ({
        url: `/team/${teamId}${dealId ? `?dealId=${dealId}` : ""}`,
        method: "PUT",
        body: { teamData: { name } },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    addPartyMembers: builder.mutation<
      any,
      {
        teamId: string;
        personIds: string[];
        roleId?: string;
        dealId: string;
        /** REP-204 stage-scoped access — grant full doc history instead of
         *  flooring the joiner at the deal's current step. */
        includeHistory?: boolean;
      }
    >({
      // dealId is sent in the body so the backend can scope its activity
      // log entry to the deal even though the route is /sides/:id/members.
      query: ({ teamId, personIds, roleId, dealId, includeHistory }) => ({
        url: `/sides/${teamId}/members`,
        method: "POST",
        body: { personIds, roleId, dealId, includeHistory },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    removePartyMember: builder.mutation<
      any,
      { teamId: string; personId: string; dealId: string }
    >({
      // dealId is passed as a query param so the backend activity log
      // points at the right deal.
      query: ({ teamId, personId, dealId }) => ({
        url: `/sides/${teamId}/members/${personId}${dealId ? `?dealId=${dealId}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    mergeParties: builder.mutation<
      any,
      { fromTeamId: string; intoTeamId: string; dealId: string }
    >({
      query: ({ fromTeamId, intoTeamId }) => ({
        url: `/sides/${fromTeamId}/merge`,
        method: "POST",
        body: { intoTeamId },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    // REP-204 — engage / walk away from a derived counterparty (the ENGAGED edge).
    engageCounterparty: builder.mutation<
      any,
      { dealId: string; sellerRepProjectId: string }
    >({
      query: ({ dealId, sellerRepProjectId }) => ({
        url: `/deals/${dealId}/engage`,
        method: "POST",
        body: { sellerRepProjectId },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
    unengageCounterparty: builder.mutation<
      any,
      { dealId: string; sellerRepProjectId: string }
    >({
      query: ({ dealId, sellerRepProjectId }) => ({
        url: `/deals/${dealId}/unengage`,
        method: "POST",
        body: { sellerRepProjectId },
      }),
      invalidatesTags: (_res, _err, { dealId }) => [
        { type: "DealParties", id: dealId },
      ],
    }),
  }),
});

export const {
  useGetDealPartiesQuery,
  useGetEffectivePermissionsQuery,
  useCreatePartyMutation,
  useRenamePartyMutation,
  useAddPartyMembersMutation,
  useRemovePartyMemberMutation,
  useMergePartiesMutation,
  useEngageCounterpartyMutation,
  useUnengageCounterpartyMutation,
} = partiesApi;
